import type { AxiosResponse } from 'axios';
import type { CloudCompassSyncKey, ConfigurationApi } from '@env0/configuration-service/api';
import type { HttpClient } from 'services/api-client/client';
import { returnData } from 'services/api-client/client';

type CreateDraftBody = ConfigurationApi.CreateCloudCompassDraft.Request.Body;
type CreateDraftResponse = ConfigurationApi.CreateCloudCompassDraft.Response;
type DraftResponse = ConfigurationApi.GetCloudCompassDraft.Response;
type VerifyDraftBody = ConfigurationApi.VerifyCloudCompassDraft.Request.Body;
type OrganizationalUnitsResponse = ConfigurationApi.ListCloudCompassOrgUnits.Response;
type CreateCredentialBody = ConfigurationApi.CreateCloudCompassCredential.Request.Body;
type CreateCredentialResponse = ConfigurationApi.CreateCloudCompassCredential.Response;
type ListCredentialsResponse = ConfigurationApi.ListCloudCompassCredentials.Response;
type CredentialResponse = ConfigurationApi.GetCloudCompassCredential.Response;
type CredentialLogsResponse = ConfigurationApi.GetCloudCompassCredentialLogs.Response;
type UpdateCredentialBody = ConfigurationApi.UpdateCloudCompassCredential.Request.Body;
type UpdateCredentialResponse = ConfigurationApi.UpdateCloudCompassCredential.Response;
type CredentialOrganizationalUnitsResponse = ConfigurationApi.ListCloudCompassCredOrgUnits.Response;

// The wizard, the credentials card and its drawer render these errors themselves, so the global toast stays off.
const inlineErrors = { hideNotification: true };

export default (client: HttpClient) => ({
  createDraft: (body: CreateDraftBody) =>
    client
      .post<CreateDraftResponse, AxiosResponse<CreateDraftResponse>, CreateDraftBody>(
        'cloud-compass/drafts',
        body,
        inlineErrors
      )
      .then(returnData),

  getDraft: (organizationId: string, draftId: string) =>
    client
      .get<DraftResponse>(`cloud-compass/drafts/${draftId}`, { params: { organizationId }, ...inlineErrors })
      .then(returnData),

  verifyDraft: (draftId: string, body: VerifyDraftBody) =>
    client
      .post<DraftResponse, AxiosResponse<DraftResponse>, VerifyDraftBody>(
        `cloud-compass/drafts/${draftId}/verify`,
        body,
        inlineErrors
      )
      .then(returnData),

  listOrganizationalUnits: (organizationId: string, draftId: string) =>
    client
      .get<OrganizationalUnitsResponse>(`cloud-compass/drafts/${draftId}/organizational-units`, {
        params: { organizationId },
        ...inlineErrors
      })
      .then(returnData),

  createCredential: (body: CreateCredentialBody) =>
    client
      .post<CreateCredentialResponse, AxiosResponse<CreateCredentialResponse>, CreateCredentialBody>(
        'cloud-compass/credentials',
        body,
        inlineErrors
      )
      .then(returnData),

  deleteDraft: (organizationId: string, draftId: string) =>
    client.delete<void>(`cloud-compass/drafts/${draftId}`, { params: { organizationId }, ...inlineErrors }),

  listCredentials: (organizationId: string) =>
    client
      .get<ListCredentialsResponse>('cloud-compass/credentials', { params: { organizationId }, ...inlineErrors })
      .then(returnData),

  getCredential: (organizationId: string, credentialId: string) =>
    client
      .get<CredentialResponse>(`cloud-compass/credentials/${credentialId}`, {
        params: { organizationId },
        ...inlineErrors
      })
      .then(returnData),

  updateCredential: (credentialId: string, body: UpdateCredentialBody) =>
    client
      .patch<UpdateCredentialResponse, AxiosResponse<UpdateCredentialResponse>, UpdateCredentialBody>(
        `cloud-compass/credentials/${credentialId}`,
        body,
        inlineErrors
      )
      .then(returnData),

  listCredentialOrganizationalUnits: (organizationId: string, credentialId: string) =>
    client
      .get<CredentialOrganizationalUnitsResponse>(`cloud-compass/credentials/${credentialId}/organizational-units`, {
        params: { organizationId },
        ...inlineErrors
      })
      .then(returnData),

  getCredentialLogs: (organizationId: string, credentialId: string, sync: CloudCompassSyncKey) =>
    client
      .get<CredentialLogsResponse>(`cloud-compass/credentials/${credentialId}/logs`, {
        params: { organizationId, sync },
        ...inlineErrors
      })
      .then(returnData),

  deleteCredential: (organizationId: string, credentialId: string) =>
    client.delete<void>(`cloud-compass/credentials/${credentialId}`, { params: { organizationId }, ...inlineErrors })
});
